import { useState, useEffect, useCallback, useMemo } from 'react';
import { admin as adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Network, Loader2, AlertTriangle, RotateCcw, Search } from 'lucide-react';

const TIER_COLORS = {
  priority: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  standard: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  economy: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  'No subscription': 'bg-zinc-700/40 text-zinc-300 border-zinc-600/40',
};

const TIER_FILTERS = ['', 'priority', 'standard', 'economy', 'No subscription'];

const ALL = '__all__';
const UNASSIGNED_VALUE = '__unassigned__';

export default function CustomerAssignments() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tierFilter, setTierFilter] = useState(ALL);
  const [serverFilter, setServerFilter] = useState(ALL);
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const [distribution, setDistribution] = useState({ servers: [], unassigned: { count: 0, label: 'Unassigned (on default)' }, total: 0, default_server_id: '' });
  const [allServers, setAllServers] = useState([]); // for the reassign dropdown options
  const [defaultServerId, setDefaultServerId] = useState('');

  // Reassign dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCustomer, setDialogCustomer] = useState(null);
  const [dialogTargetServer, setDialogTargetServer] = useState('');
  const [dialogSaving, setDialogSaving] = useState(false);

  const loadDistribution = useCallback(async () => {
    try {
      const res = await adminApi.getAssignmentDistribution();
      setDistribution(res.data);
      setDefaultServerId(res.data.default_server_id || '');
    } catch (err) {
      // non-blocking
    }
  }, []);

  // The "All Servers" list for the reassign Select (uses /admin/servers).
  const loadServerList = useCallback(async () => {
    try {
      const { default: api } = await import('@/lib/api');
      const res = await api.get('/admin/servers');
      setAllServers(res.data.servers || []);
    } catch (err) { /* non-blocking */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: pageSize };
      if (search) params.search = search;
      if (tierFilter !== ALL) params.tier = tierFilter;
      if (serverFilter !== ALL) {
        if (serverFilter === UNASSIGNED_VALUE) params.unassigned = true;
        else params.server_id = serverFilter;
      } else if (unassignedOnly) {
        params.unassigned = true;
      }
      const res = await adminApi.getCustomerAssignments(params);
      setRows(res.data.customers || []);
      setTotal(res.data.total || 0);
      setPageSize(res.data.limit || res.data.page_size || 50);
      setDefaultServerId(res.data.default_server_id || defaultServerId);
    } catch (err) {
      toast.error('Failed to load customer assignments');
    }
    setLoading(false);
  }, [skip, pageSize, search, tierFilter, serverFilter, unassignedOnly, defaultServerId]);

  useEffect(() => { loadServerList(); loadDistribution(); }, [loadServerList, loadDistribution]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    setSkip(0);
    setSearch(searchInput.trim());
  };

  const openAssign = (customer) => {
    setDialogCustomer(customer);
    setDialogTargetServer(customer.assigned_server_id || defaultServerId);
    setDialogOpen(true);
  };

  const openUnassign = (customer) => {
    setDialogCustomer(customer);
    setDialogTargetServer(defaultServerId);
    setDialogOpen(true);
  };

  const doAssign = async () => {
    if (!dialogCustomer || !dialogTargetServer) return;
    setDialogSaving(true);
    try {
      const res = await adminApi.updateCustomerAssignment(dialogCustomer.user_id, { server_id: dialogTargetServer });
      const w = res.data?.warning;
      if (w) toast.warning(w, { duration: 7000 });
      else toast.success(`${dialogCustomer.email} → ${res.data.placement_label}`);
      setDialogOpen(false);
      setDialogCustomer(null);
      await Promise.all([loadRows(), loadDistribution()]);
    } catch (err) {
      const detail = err?.response?.data;
      const msg = typeof detail === 'string' ? detail : (detail?.detail?.message || detail?.detail || detail?.message || 'Assignment failed');
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setDialogSaving(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(skip / pageSize) + 1;

  const activeServerOptions = useMemo(
    () => allServers.filter((s) => s.status === 'active'),
    [allServers]
  );

  return (
    <div data-testid="customer-assignments-page" className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Network className="w-6 h-6 text-emerald-400" />
        <div>
          <h1 className="text-2xl font-bold" data-testid="customer-assignments-title">Customer Mapping</h1>
          <p className="text-sm text-muted-foreground">
            Manually assign customers to GPU servers. Everyone defaults to{' '}
            <span className="font-mono text-foreground">{defaultServerId || '—'}</span> and shows as
            "Unassigned" until placed on a role-specific server.
          </p>
        </div>
      </div>

      {/* Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="assignment-distribution">
        <Card data-testid="distribution-unassigned">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{distribution.unassigned?.label || 'Unassigned (on default)'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="distribution-unassigned-count">{distribution.unassigned?.count ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">on default: {distribution.default_server_id || '—'}</div>
          </CardContent>
        </Card>
        {(distribution.servers || []).map((s) => (
          <Card key={s.server_id} data-testid={`distribution-server-${s.server_id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{s.display_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{s.count}</div>
              <div className="text-xs text-muted-foreground mt-1">role: {s.server_role} · status: {s.status}</div>
            </CardContent>
          </Card>
        ))}
        <Card data-testid="distribution-total">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total customers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{distribution.total ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">non-admin accounts</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <form onSubmit={submitSearch} className="flex gap-2 md:col-span-2">
            <div className="flex-1">
              <Label className="text-xs">Search</Label>
              <Input
                data-testid="search-input"
                placeholder="email / name / company…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Button type="submit" variant="secondary" data-testid="search-submit">
              <Search className="w-4 h-4 mr-1" /> Search
            </Button>
          </form>
          <div>
            <Label className="text-xs">Tier</Label>
            <Select value={tierFilter} onValueChange={(v) => { setSkip(0); setTierFilter(v); }}>
              <SelectTrigger data-testid="filter-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All tiers</SelectItem>
                {TIER_FILTERS.filter(Boolean).map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Placement</Label>
            <Select value={serverFilter} onValueChange={(v) => { setSkip(0); setServerFilter(v); }}>
              <SelectTrigger data-testid="filter-placement"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All placements</SelectItem>
                <SelectItem value={UNASSIGNED_VALUE}>Unassigned (on default)</SelectItem>
                {allServers.filter((s) => s.server_id !== defaultServerId).map((s) => (
                  <SelectItem key={s.server_id} value={s.server_id}>{s.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading customers…
            </div>
          ) : (
            <Table data-testid="customers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Placement</TableHead>
                  <TableHead>Assigned at</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                      No customers match the current filters.
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`row-${r.user_id}`}>
                    <TableCell>
                      <div className="font-medium" data-testid={`row-email-${r.user_id}`}>{r.email}</div>
                      {r.full_name && <div className="text-xs text-muted-foreground">{r.full_name}</div>}
                      {r.company_name && <div className="text-xs text-muted-foreground">{r.company_name}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={TIER_COLORS[r.tier] || ''} data-testid={`row-tier-${r.user_id}`}>{r.tier}</Badge>
                    </TableCell>
                    <TableCell>
                      {r.is_unassigned ? (
                        <Badge variant="outline" className="bg-zinc-700/40 text-zinc-300 border-zinc-600/40" data-testid={`row-placement-${r.user_id}`}>
                          Unassigned
                        </Badge>
                      ) : (
                        <span data-testid={`row-placement-${r.user_id}`}>{r.placement_label}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.server_assigned_at ? new Date(r.server_assigned_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="secondary" onClick={() => openAssign(r)} data-testid={`assign-btn-${r.user_id}`}>
                        Assign…
                      </Button>
                      {!r.is_unassigned && (
                        <Button size="sm" variant="outline" onClick={() => openUnassign(r)} data-testid={`unassign-btn-${r.user_id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Unassign
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground" data-testid="pagination-info">
          {total === 0 ? 'No results' : `Showing ${skip + 1}–${Math.min(skip + pageSize, total)} of ${total}`}
        </div>
        <div className="space-x-2">
          <Button size="sm" variant="outline" disabled={skip <= 0}
                  onClick={() => setSkip(Math.max(0, skip - pageSize))}
                  data-testid="pagination-prev">Prev</Button>
          <span className="text-xs text-muted-foreground">Page {currentPage} / {pages}</span>
          <Button size="sm" variant="outline" disabled={skip + pageSize >= total}
                  onClick={() => setSkip(skip + pageSize)}
                  data-testid="pagination-next">Next</Button>
        </div>
      </div>

      {/* Reassign dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!dialogSaving) { setDialogOpen(o); if (!o) setDialogCustomer(null); } }}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>Reassign customer</DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span>Reassigning <span className="font-mono text-foreground">{dialogCustomer?.email}</span>.</span>
              <span className="block text-amber-400">
                <AlertTriangle className="inline w-4 h-4 mr-1" />
                Reassigning changes routing only. It does NOT migrate the customer's existing data,
                which stays on their current server.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Label>Target server</Label>
            <Select value={dialogTargetServer} onValueChange={setDialogTargetServer}>
              <SelectTrigger data-testid="reassign-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {activeServerOptions.map((s) => (
                  <SelectItem key={s.server_id} value={s.server_id} data-testid={`reassign-option-${s.server_id}`}>
                    {s.display_name}{s.server_id === defaultServerId ? ' (default → "Unassigned")' : ` · ${s.server_role}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">
              Current placement: <span className="font-mono">{dialogCustomer?.placement_label || '—'}</span>
              {dialogCustomer?.tier && <> · tier <span className="font-mono">{dialogCustomer.tier}</span></>}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={dialogSaving} data-testid="reassign-cancel">Cancel</Button>
            <Button onClick={doAssign} disabled={dialogSaving || !dialogTargetServer} data-testid="reassign-confirm">
              {dialogSaving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Confirm reassignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
